import { useRef, useState, useEffect, type ReactNode } from "react";
import PiChevronDownStroke from "@markeroo/icons/src/stroke/arrows-&-chevrons/PiChevronDownStroke";
import PiChevronUpStroke from "@markeroo/icons/src/stroke/arrows-&-chevrons/PiChevronUpStroke";
import { zIndex } from "@/styles/variants";
import { buttonVariants } from "@markeroo/theme";
import { useRovingTabindex } from "@/hooks/useRovingTabindex";

export interface DropdownItem {
  key: string;
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}

interface DropdownProps {
  label: string;
  items: DropdownItem[];
  variant?: keyof typeof buttonVariants;
  disabled?: boolean;
  align?: "left" | "right";
  direction?: "up" | "down";
}

export function Dropdown({
  label,
  items,
  variant = "secondary",
  disabled = false,
  align = "left",
  direction = "down",
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Set up roving tabindex for keyboard navigation
  const { getTabIndex, handleKeyDown, handleFocus } = useRovingTabindex({
    containerRef: menuRef,
    enabled: isOpen,
    orientation: "vertical",
    wrap: true,
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleItemClick = (item: DropdownItem) => {
    if (!item.disabled) {
      item.onClick();
      setIsOpen(false);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        ref={buttonRef}
        className={`${buttonVariants[variant]} gap-2`}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        disabled={disabled}
      >
        <span>{label}</span>
        {direction === "up" ? (
          <PiChevronUpStroke size={16} />
        ) : (
          <PiChevronDownStroke size={16} />
        )}
      </button>
      {isOpen && (
        <div
          ref={menuRef}
          role="menu"
          className={`absolute min-w-full w-max rounded-xl bg-white p-1 shadow-card ${
            align === "right" ? "right-0" : "left-0"
          } ${direction === "up" ? "bottom-full mb-2" : "top-full mt-2"}`}
          style={{ zIndex: zIndex.dropdown }}
          onKeyDown={handleKeyDown}
        >
          {items.map((item, index) => (
            <button
              key={item.key}
              role="menuitem"
              type="button"
              tabIndex={getTabIndex(index)}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-text-theme-primary transition hover:bg-surface-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => handleItemClick(item)}
              onFocus={() => handleFocus(index)}
              disabled={item.disabled}
            >
              {item.icon && <span className="w-4 h-4 flex-shrink-0">{item.icon}</span>}
              <span className="whitespace-nowrap">{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
